import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { get, reject, isEmpty } from "lodash";
import Table, { TableHeading, TableControls, TableFooter } from "@/components/Table";
import SearchInput from "@/components/SearchInput";
import Select from "@/components/Select";
import FacilityAutocomplete from "./_components/FacilityAutocomplete";
import MultiSelect from "@/components/MultiSelect";
import EmptyState from "@/components/EmptyState";
import MetricGrid from "@/components/MetricGrid";
import usePagination from "@/hooks/usePagination";
import { useSearchWithParams, useParams as useUrlParams } from "@/hooks/useSearchParams";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import usePageTitle from "@/hooks/usePageTitle";
import useTableRowMenu from "@/hooks/useTableRowMenu";
import { toFormat } from "@/lib/dateHelpers";
import { ALL_US_STATES_ABBRV } from "@/lib/usStateHelpers";
import type { Path } from "@/router";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import { GET_CLASSIFICATIONS } from "@/pages/billing/queries";
import GrpTabLayout from "../_GrpTabLayout";
import { GRP_ENROLLMENTS_QUERY } from "./queries";
import { enrollmentPersonId, getColumns } from "./_components/TableColumns";
import {
  GrpEnrollmentStatus,
  PENDING_DOLLARS_PLACEHOLDER,
  type GrpEnrollmentNode,
  type GrpEnrollmentRow
} from "./types";

const STATUS_FILTER_OPTIONS = [
  { value: "ALL", label: "All" },
  { value: GrpEnrollmentStatus.ACTIVE, label: "Active" },
  { value: GrpEnrollmentStatus.UNENROLLED, label: "Unenrolled" }
];

const stateOptions = ALL_US_STATES_ABBRV.map((value) => ({ value, label: value }));

function parseCommaSeparatedParam(value: string | string[] | undefined): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return value.split(",").filter(Boolean);
}

function mapEnrollmentRows(nodes: GrpEnrollmentNode[]): GrpEnrollmentRow[] {
  return nodes.map((node) => ({
    ...node,
    pendingDollars: PENDING_DOLLARS_PLACEHOLDER,
    enrolledOnLabel: toFormat(node.accountOpenedOn) || node.accountOpenedOn
  }));
}

export default function GrpEnrollmentsPage() {
  usePageTitle("/billing/finance/grp/enrollments", "Enrollments");
  useRequireCapability({ domain: DOMAIN.BENEFIT_PROGRAMS, permission: PERMISSION.viewGrpEnrollments });
  const { isCompactLayout } = useIsCompactLayout();
  const { Menu, setMenu } = useTableRowMenu();
  const [isPersonSideSheetOpen, setIsPersonSideSheetOpen] = useState(false);
  const [selectedPgaId, setSelectedPgaId] = useState<string | null>(null);
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({ paramName: "search" });
  const { params, addSearchParam, deleteSearchParams } = useUrlParams();
  const status = params.status as GrpEnrollmentStatus | undefined;
  const facilityName = params.facilityName as string | undefined;
  const [selectedClassifications, setSelectedClassifications] = useState<string[]>(
    parseCommaSeparatedParam(params.classifications)
  );
  const [selectedStates, setSelectedStates] = useState<string[]>(parseCommaSeparatedParam(params.states));

  const { data: classificationData } = useQuery(GET_CLASSIFICATIONS);

  const classificationsParam = Array.isArray(params.classifications)
    ? params.classifications.join(",")
    : (params.classifications ?? "");

  useEffect(() => {
    setSelectedClassifications(parseCommaSeparatedParam(classificationsParam));
  }, [classificationsParam]);

  useEffect(() => {
    setSelectedStates(parseCommaSeparatedParam(params.states));
  }, [params.states]);

  useEffect(() => {
    resetPagination();
  }, [debouncedSearch, status, facilityName, selectedClassifications, selectedStates, resetPagination]);

  const filters = {
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(status ? { status } : {}),
    ...(facilityName ? { facilityName } : {}),
    ...(selectedClassifications.length ? { classifications: selectedClassifications } : {}),
    ...(selectedStates.length ? { states: selectedStates } : {})
  };

  const { data, previousData, loading, error } = useQuery(GRP_ENROLLMENTS_QUERY, {
    variables: {
      ...paginationVariables,
      filters: Object.keys(filters).length ? filters : undefined
    }
  });

  const connection = data?.grpEnrollments ?? previousData?.grpEnrollments;
  const items = useMemo(() => mapEnrollmentRows(connection?.nodes ?? []), [connection?.nodes]);
  const totalRowCount = connection?.totalCount ?? items.length;
  const columns = useMemo(
    () =>
      getColumns({
        isCompactLayout,
        setMenu,
        onViewPerson: (pgaId) => {
          setSelectedPgaId(pgaId);
          setIsPersonSideSheetOpen(true);
        }
      }),
    [isCompactLayout, setMenu]
  );
  const metrics = data?.grpEnrollmentMetrics;
  const hasActiveFilters =
    !!debouncedSearch || !!status || !!facilityName || selectedClassifications.length > 0 || selectedStates.length > 0;
  const activeFilterCount = [debouncedSearch, status, facilityName, selectedClassifications, selectedStates].filter(
    (value) => (Array.isArray(value) ? value.length > 0 : !!value)
  ).length;
  const classificationOptions =
    get(classificationData, "classifications.nodes")?.map((node: { name: string }) => ({
      value: node.name,
      label: node.name
    })) ?? [];

  function setStatus(value: GrpEnrollmentStatus | undefined) {
    value ? addSearchParam("status", value) : deleteSearchParams("status");
    resetPagination();
  }

  function setFacilityName(value: string | undefined) {
    value ? addSearchParam("facilityName", value) : deleteSearchParams("facilityName");
    resetPagination();
  }

  function setClassifications(values: string[]) {
    const nextValues = reject(values, isEmpty);
    setSelectedClassifications(nextValues);
    if (nextValues.length > 0) {
      addSearchParam("classifications", nextValues.join(","));
    } else {
      deleteSearchParams("classifications");
    }
    resetPagination();
  }

  function setStates(values: string[]) {
    const nextValues = reject(values, isEmpty);
    setSelectedStates(nextValues);
    if (nextValues.length > 0) {
      addSearchParam("states", nextValues.join(","));
    } else {
      deleteSearchParams("states");
    }
    resetPagination();
  }

  if (error) {
    return (
      <GrpTabLayout>
        <EmptyState title="Error" caption="An error occurred while loading enrollments." iconName="error" />
      </GrpTabLayout>
    );
  }

  return (
    <GrpTabLayout>
      <Menu />
      <MetricGrid
        hasHeading
        metrics={[
          {
            label: "Active (full roster)",
            value: metrics ? String(metrics.activeCount) : undefined,
            loading,
            iconName: "person",
            tooltip: {
              supportingText: "Full Account File total. Table search and filters do not change this count."
            }
          },
          {
            label: "Unenrolled (full roster)",
            value: metrics ? String(metrics.unenrolledCount) : undefined,
            loading,
            iconName: "person",
            tooltip: {
              supportingText: "Full Account File total. Table search and filters do not change this count."
            }
          },
          {
            label: "Pending dollars",
            value: PENDING_DOLLARS_PLACEHOLDER,
            iconName: "paid"
          }
        ]}
      />
      <TableHeading heading="Enrollments" body="Members who have appeared on a Corebridge Account File." />
      <Table
        data={items}
        columns={columns}
        loading={loading}
        hasActiveFilters={hasActiveFilters}
        getRowLink={(row) => {
          const pgaId = enrollmentPersonId(row.original);
          return pgaId ? { to: `/people/${pgaId}` as Path } : null;
        }}
        renderNoDataEmptyState={() => (
          <EmptyState title="No Enrollments Yet." caption="The Account File roster is empty." iconName="list" />
        )}
        renderFilteredEmptyState={() => (
          <EmptyState title="No Results." caption="Try a different search or filter." iconName="search" />
        )}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls
            pagination={createHeaderPagination(connection, {
              loading,
              pageCount: items.length,
              totalCount: totalRowCount
            })}
            onSortClick={onSortClick}
            useExpandedFilters
            expandedFiltersState={{
              status: !!status,
              facilityName: !!facilityName,
              classifications: selectedClassifications.length > 0,
              states: selectedStates.length > 0
            }}
            activeFilters={activeFilterCount}
            onClearAllFilters={() => {
              clearSearch();
              deleteSearchParams(["status", "facilityName", "classifications", "states"]);
              setSelectedClassifications([]);
              setSelectedStates([]);
              resetPagination();
            }}
          >
            <SearchInput
              query={searchValue}
              onChange={(value) => {
                setSearchValue(value);
                resetPagination();
              }}
              onClear={() => {
                clearSearch();
                resetPagination();
              }}
              placeholder="Search Name or PGA ID"
            />
            <div data-name="facilityName">
              <FacilityAutocomplete
                facilityName={facilityName}
                onSelect={setFacilityName}
                onClear={() => setFacilityName(undefined)}
              />
            </div>
            <div data-name="status">
              <Select
                label="Status"
                value={status || "ALL"}
                onSelect={(value) => setStatus(value === "ALL" ? undefined : (value as GrpEnrollmentStatus))}
                options={STATUS_FILTER_OPTIONS}
              />
            </div>
            <div data-name="classifications">
              <MultiSelect
                label="Class"
                selectedValues={selectedClassifications}
                options={classificationOptions}
                onSelect={(value, checked) => {
                  const nextValues = checked
                    ? [...selectedClassifications, value]
                    : selectedClassifications.filter((classification) => classification !== value);
                  setClassifications(nextValues);
                }}
                onClear={() => setClassifications([])}
              />
            </div>
            <div data-name="states">
              <MultiSelect
                label="State"
                selectedValues={selectedStates}
                options={stateOptions}
                onSelect={(value, checked) => {
                  const nextValues = checked
                    ? [...selectedStates, value]
                    : selectedStates.filter((state) => state !== value);
                  setStates(nextValues);
                }}
                onClear={() => setStates([])}
              />
            </div>
          </TableControls>
        )}
        renderFooter={() => (
          <TableFooter
            pagination={createFooterPagination(connection, {
              loading,
              pageCount: items.length,
              totalCount: totalRowCount
            })}
            totalRowCount={totalRowCount}
          />
        )}
      />
      <PersonSideSheet
        open={isPersonSideSheetOpen}
        onClose={() => {
          setIsPersonSideSheetOpen(false);
          setSelectedPgaId(null);
        }}
        pgaId={selectedPgaId}
        showPersonalDetails
        showEmployment
        showFacility
      />
    </GrpTabLayout>
  );
}
